import {
    type ApiError,
    type AssumptionSet,
    type AssumptionSetState,
    type AssumptionSetSummary,
} from '@lightdash/common';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { lightdashApi } from '../../api';

const base = (projectUuid: string, dealId: string) =>
    `/projects/${projectUuid}/deals/${encodeURIComponent(dealId)}/assumption-sets`;

type Key = {
    projectUuid: string;
    dealId: string | undefined;
    module: string;
    inputs: Record<string, string>;
};

export const useAssumptionSetState = ({ projectUuid, dealId, module, inputs }: Key) =>
    useQuery<AssumptionSetState, ApiError>({
        queryKey: ['assumption-set-state', projectUuid, dealId, module, inputs],
        enabled: !!dealId,
        queryFn: () =>
            lightdashApi<AssumptionSetState>({
                url: `${base(projectUuid, dealId!)}/state`,
                method: 'POST',
                body: JSON.stringify({ module, inputs }),
            }),
    });

export const useAssumptionSets = (
    projectUuid: string,
    dealId: string | undefined,
    module: string,
    enabled: boolean,
) =>
    useQuery<AssumptionSetSummary[], ApiError>({
        queryKey: ['assumption-sets', projectUuid, dealId, module],
        enabled: enabled && !!dealId,
        queryFn: () =>
            lightdashApi<AssumptionSetSummary[]>({
                url: `${base(projectUuid, dealId!)}/?module=${module}`,
                method: 'GET',
                body: undefined,
            }),
    });

export const useAssumptionSet = (
    projectUuid: string,
    dealId: string | undefined,
    uuid: string | undefined,
) =>
    useQuery<AssumptionSet, ApiError>({
        queryKey: ['assumption-set', projectUuid, uuid],
        enabled: !!dealId && !!uuid,
        queryFn: () =>
            lightdashApi<AssumptionSet>({
                url: `${base(projectUuid, dealId!)}/${uuid}`,
                method: 'GET',
                body: undefined,
            }),
    });

const useRefresh = () => {
    const queryClient = useQueryClient();
    return () =>
        Promise.all([
            queryClient.invalidateQueries({ queryKey: ['assumption-set-state'] }),
            queryClient.invalidateQueries({ queryKey: ['assumption-sets'] }),
        ]);
};

export const usePublishAssumptionSet = ({ projectUuid, dealId, module, inputs }: Key) => {
    const refresh = useRefresh();
    return useMutation<AssumptionSet, ApiError>({
        mutationFn: () =>
            lightdashApi<AssumptionSet>({
                url: `${base(projectUuid, dealId!)}/`,
                method: 'POST',
                body: JSON.stringify({ module, inputs }),
            }),
        onSuccess: refresh,
    });
};

export const useApproveAssumptionSet = (projectUuid: string, dealId: string | undefined) => {
    const refresh = useRefresh();
    return useMutation<AssumptionSet, ApiError, { uuid: string; reviewConfirmed: string[] }>({
        mutationFn: ({ uuid, reviewConfirmed }) =>
            lightdashApi<AssumptionSet>({
                url: `${base(projectUuid, dealId!)}/${uuid}/approve`,
                method: 'POST',
                body: JSON.stringify({ reviewConfirmed }),
            }),
        onSuccess: refresh,
    });
};
